// Mock database service that simulates Firestore without needing emulators
class MockDatabase {
  constructor() {
    this.data = {
      organizations: [],
      locations: [],
      buildings: [],
      floors: [],
      departments: [],
      rooms: [],
      beds: [],
      personnel: [],
      equipment: [],
      biological_entities: [],
      ipcConfiguration: [],
      users: [],
      roles: [],
      userRoles: [],
      departmentAssignments: [],
      userStatusHistory: [],
      shiftAssignments: [],
      emergencyContacts: [],
      userAvailability: [],
      rolePermissions: [],
      auditLogs: []
    };
    this.nextId = 1;
  }

  generateId() {
    return `mock-${this.nextId++}`;
  }

  async create(collection, data) {
    const id = this.generateId();
    const record = {
      id,
      ...data,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.data[collection].push(record);
    console.log(`✅ Created ${collection}:`, id);
    return record;
  }

  async getAll(collection) {
    return [...this.data[collection]];
  }

  async getById(collection, id) {
    return this.data[collection].find(item => item.id === id);
  }

  async update(collection, id, data) {
    const index = this.data[collection].findIndex(item => item.id === id);
    if (index !== -1) {
      this.data[collection][index] = {
        ...this.data[collection][index],
        ...data,
        updatedAt: new Date().toISOString()
      };
      return this.data[collection][index];
    }
    throw new Error(`Record ${id} not found in ${collection}`);
  }

  async delete(collection, id) {
    const index = this.data[collection].findIndex(item => item.id === id);
    if (index !== -1) {
      this.data[collection].splice(index, 1);
      return { id };
    }
    throw new Error(`Record ${id} not found in ${collection}`);
  }

  async getWhere(collection, field, operator, value) {
    return this.data[collection].filter(item => {
      switch (operator) {
        case '==':
          return item[field] === value;
        case '!=':
          return item[field] !== value;
        case '>':
          return item[field] > value;
        case '<':
          return item[field] < value;
        case '>=':
          return item[field] >= value;
        case '<=':
          return item[field] <= value;
        case 'array-contains':
          return Array.isArray(item[field]) && item[field].includes(value);
        default:
          return false;
      }
    });
  }

  // Get counts for dashboard
  getCounts() {
    return {
      organizations: this.data.organizations.length,
      locations: this.data.locations.length,
      buildings: this.data.buildings.length,
      floors: this.data.floors.length,
      departments: this.data.departments.length,
      rooms: this.data.rooms.length,
      beds: this.data.beds.length,
      personnel: this.data.personnel.length,
      equipment: this.data.equipment.length,
      biological_entities: this.data.biological_entities.length,
      ipcConfiguration: this.data.ipcConfiguration.length,
      users: this.data.users.length,
      roles: this.data.roles.length,
      userRoles: this.data.userRoles.length,
      departmentAssignments: this.data.departmentAssignments.length,
      userStatusHistory: this.data.userStatusHistory.length,
      shiftAssignments: this.data.shiftAssignments.length,
      emergencyContacts: this.data.emergencyContacts.length,
      userAvailability: this.data.userAvailability.length
    };
  }

  // Clear all data
  clearAll() {
    Object.keys(this.data).forEach(key => {
      this.data[key] = [];
    });
    this.nextId = 1;
    console.log('🧹 All data cleared');
  }
}

// Create singleton instance
const mockDb = new MockDatabase();

// Export services that match Firebase service interface
export const organizationService = {
  create: (data) => mockDb.create('organizations', data),
  getAll: () => mockDb.getAll('organizations'),
  getById: (id) => mockDb.getById('organizations', id),
  update: (id, data) => mockDb.update('organizations', id, data),
  delete: (id) => mockDb.delete('organizations', id),
  getWhere: (field, operator, value) => mockDb.getWhere('organizations', field, operator, value)
};

export const locationService = {
  create: (data) => mockDb.create('locations', data),
  getAll: () => mockDb.getAll('locations'),
  getById: (id) => mockDb.getById('locations', id),
  update: (id, data) => mockDb.update('locations', id, data),
  delete: (id) => mockDb.delete('locations', id),
  getWhere: (field, operator, value) => mockDb.getWhere('locations', field, operator, value)
};

export const buildingService = {
  create: (data) => mockDb.create('buildings', data),
  getAll: () => mockDb.getAll('buildings'),
  getById: (id) => mockDb.getById('buildings', id),
  update: (id, data) => mockDb.update('buildings', id, data),
  delete: (id) => mockDb.delete('buildings', id),
  getWhere: (field, operator, value) => mockDb.getWhere('buildings', field, operator, value)
};

export const floorService = {
  create: (data) => mockDb.create('floors', data),
  getAll: () => mockDb.getAll('floors'),
  getById: (id) => mockDb.getById('floors', id),
  update: (id, data) => mockDb.update('floors', id, data),
  delete: (id) => mockDb.delete('floors', id),
  getWhere: (field, operator, value) => mockDb.getWhere('floors', field, operator, value)
};

export const departmentService = {
  create: (data) => mockDb.create('departments', data),
  getAll: () => mockDb.getAll('departments'),
  getById: (id) => mockDb.getById('departments', id),
  update: (id, data) => mockDb.update('departments', id, data),
  delete: (id) => mockDb.delete('departments', id),
  getWhere: (field, operator, value) => mockDb.getWhere('departments', field, operator, value)
};

export const roomService = {
  create: (data) => mockDb.create('rooms', data),
  getAll: () => mockDb.getAll('rooms'),
  getById: (id) => mockDb.getById('rooms', id),
  update: (id, data) => mockDb.update('rooms', id, data),
  delete: (id) => mockDb.delete('rooms', id),
  getWhere: (field, operator, value) => mockDb.getWhere('rooms', field, operator, value)
};

export const bedService = {
  create: (data) => mockDb.create('beds', data),
  getAll: () => mockDb.getAll('beds'),
  getById: (id) => mockDb.getById('beds', id),
  update: (id, data) => mockDb.update('beds', id, data),
  delete: (id) => mockDb.delete('beds', id),
  getWhere: (field, operator, value) => mockDb.getWhere('beds', field, operator, value)
};

export const personnelService = {
  create: (data) => mockDb.create('personnel', data),
  getAll: () => mockDb.getAll('personnel'),
  getById: (id) => mockDb.getById('personnel', id),
  update: (id, data) => mockDb.update('personnel', id, data),
  delete: (id) => mockDb.delete('personnel', id),
  getWhere: (field, operator, value) => mockDb.getWhere('personnel', field, operator, value)
};

export const equipmentService = {
  create: (data) => mockDb.create('equipment', data),
  getAll: () => mockDb.getAll('equipment'),
  getById: (id) => mockDb.getById('equipment', id),
  update: (id, data) => mockDb.update('equipment', id, data),
  delete: (id) => mockDb.delete('equipment', id),
  getWhere: (field, operator, value) => mockDb.getWhere('equipment', field, operator, value)
};

export const biologicalEntityService = {
  create: (data) => mockDb.create('biological_entities', data),
  getAll: () => mockDb.getAll('biological_entities'),
  getById: (id) => mockDb.getById('biological_entities', id),
  update: (id, data) => mockDb.update('biological_entities', id, data),
  delete: (id) => mockDb.delete('biological_entities', id),
  getWhere: (field, operator, value) => mockDb.getWhere('biological_entities', field, operator, value)
};

export const ipcConfigurationService = {
  create: (data) => mockDb.create('ipcConfiguration', data),
  getAll: () => mockDb.getAll('ipcConfiguration'),
  getById: (id) => mockDb.getById('ipcConfiguration', id),
  update: (id, data) => mockDb.update('ipcConfiguration', id, data),
  delete: (id) => mockDb.delete('ipcConfiguration', id),
  getWhere: (field, operator, value) => mockDb.getWhere('ipcConfiguration', field, operator, value)
};

// User Management Services
export const userService = {
  create: (data) => mockDb.create('users', data),
  getAll: () => mockDb.getAll('users'),
  getById: (id) => mockDb.getById('users', id),
  update: (id, data) => mockDb.update('users', id, data),
  delete: (id) => mockDb.delete('users', id),
  getWhere: (field, operator, value) => mockDb.getWhere('users', field, operator, value),
  getByBadgeId: (badgeId) => mockDb.getWhere('users', 'badge_id', '==', badgeId),
  getByEmployeeId: (employeeId) => mockDb.getWhere('users', 'employee_id', '==', employeeId),
  getByEmail: (email) => mockDb.getWhere('users', 'email', '==', email),
  getByDepartment: (departmentId) => {
    const primary = mockDb.getWhere('users', 'primary_department_id', '==', departmentId);
    const secondary = mockDb.getWhere('users', 'secondary_department_ids', 'array-contains', departmentId);
    return [...primary, ...secondary];
  },
  search: (searchTerm) => {
    const allUsers = mockDb.getAll('users');
    const term = searchTerm.toLowerCase();
    return allUsers.filter(user => 
      user.first_name?.toLowerCase().includes(term) ||
      user.last_name?.toLowerCase().includes(term) ||
      user.full_name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.employee_id?.toLowerCase().includes(term) ||
      user.badge_id?.toLowerCase().includes(term)
    );
  }
};

export const roleService = {
  create: (data) => mockDb.create('roles', data),
  getAll: () => mockDb.getAll('roles'),
  getById: (id) => mockDb.getById('roles', id),
  update: (id, data) => mockDb.update('roles', id, data),
  delete: (id) => mockDb.delete('roles', id),
  getWhere: (field, operator, value) => mockDb.getWhere('roles', field, operator, value)
};

export const userRoleService = {
  create: (data) => mockDb.create('userRoles', data),
  getAll: () => mockDb.getAll('userRoles'),
  getById: (id) => mockDb.getById('userRoles', id),
  update: (id, data) => mockDb.update('userRoles', id, data),
  delete: (id) => mockDb.delete('userRoles', id),
  getWhere: (field, operator, value) => mockDb.getWhere('userRoles', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('userRoles', 'user_id', '==', userId),
  getActiveByUserId: (userId) => {
    const userRoles = mockDb.getWhere('userRoles', 'user_id', '==', userId);
    const now = new Date();
    return userRoles.filter(role => 
      new Date(role.effective_date) <= now && 
      (!role.end_date || new Date(role.end_date) > now)
    );
  }
};

export const departmentAssignmentService = {
  create: (data) => mockDb.create('departmentAssignments', data),
  getAll: () => mockDb.getAll('departmentAssignments'),
  getById: (id) => mockDb.getById('departmentAssignments', id),
  update: (id, data) => mockDb.update('departmentAssignments', id, data),
  delete: (id) => mockDb.delete('departmentAssignments', id),
  getWhere: (field, operator, value) => mockDb.getWhere('departmentAssignments', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('departmentAssignments', 'user_id', '==', userId),
  getByDepartmentId: (departmentId) => mockDb.getWhere('departmentAssignments', 'department_id', '==', departmentId),
  getActiveByUserId: (userId) => {
    const assignments = mockDb.getWhere('departmentAssignments', 'user_id', '==', userId);
    const now = new Date();
    return assignments.filter(assignment => 
      new Date(assignment.effective_date) <= now && 
      (!assignment.end_date || new Date(assignment.end_date) > now)
    );
  }
};

export const userStatusService = {
  create: (data) => mockDb.create('userStatusHistory', data),
  getAll: () => mockDb.getAll('userStatusHistory'),
  getById: (id) => mockDb.getById('userStatusHistory', id),
  update: (id, data) => mockDb.update('userStatusHistory', id, data),
  delete: (id) => mockDb.delete('userStatusHistory', id),
  getWhere: (field, operator, value) => mockDb.getWhere('userStatusHistory', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('userStatusHistory', 'user_id', '==', userId),
  getCurrentStatus: (userId) => {
    const statusHistory = mockDb.getWhere('userStatusHistory', 'user_id', '==', userId);
    return statusHistory.sort((a, b) => new Date(b.status_change_date) - new Date(a.status_change_date))[0];
  }
};

export const shiftAssignmentService = {
  create: (data) => mockDb.create('shiftAssignments', data),
  getAll: () => mockDb.getAll('shiftAssignments'),
  getById: (id) => mockDb.getById('shiftAssignments', id),
  update: (id, data) => mockDb.update('shiftAssignments', id, data),
  delete: (id) => mockDb.delete('shiftAssignments', id),
  getWhere: (field, operator, value) => mockDb.getWhere('shiftAssignments', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('shiftAssignments', 'user_id', '==', userId),
  getByDepartmentId: (departmentId) => mockDb.getWhere('shiftAssignments', 'department_id', '==', departmentId)
};

export const emergencyContactService = {
  create: (data) => mockDb.create('emergencyContacts', data),
  getAll: () => mockDb.getAll('emergencyContacts'),
  getById: (id) => mockDb.getById('emergencyContacts', id),
  update: (id, data) => mockDb.update('emergencyContacts', id, data),
  delete: (id) => mockDb.delete('emergencyContacts', id),
  getWhere: (field, operator, value) => mockDb.getWhere('emergencyContacts', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('emergencyContacts', 'user_id', '==', userId)
};

export const userAvailabilityService = {
  create: (data) => mockDb.create('userAvailability', data),
  getAll: () => mockDb.getAll('userAvailability'),
  getById: (id) => mockDb.getById('userAvailability', id),
  update: (id, data) => mockDb.update('userAvailability', id, data),
  delete: (id) => mockDb.delete('userAvailability', id),
  getWhere: (field, operator, value) => mockDb.getWhere('userAvailability', field, operator, value),
  getByUserId: (userId) => mockDb.getWhere('userAvailability', 'user_id', '==', userId),
  getAvailableUsers: (departmentId) => {
    const allAvailability = mockDb.getAll('userAvailability');
    return allAvailability.filter(avail => 
      avail.available === true && 
      avail.department_context === departmentId
    );
  }
};

// Role Management Services
export const roleManagementService = {
  create: (data) => mockDb.create('userRoles', data),
  getAll: () => mockDb.getAll('userRoles'),
  getById: (id) => mockDb.getById('userRoles', id),
  update: (id, data) => mockDb.update('userRoles', id, data),
  delete: (id) => mockDb.delete('userRoles', id),
  getByUserId: (userId) => mockDb.getWhere('userRoles', 'user_id', '==', userId),
  getActiveByUserId: (userId) => {
    const userRoles = mockDb.getWhere('userRoles', 'user_id', '==', userId);
    return userRoles.filter(role => !role.end_date);
  }
};

// Permission Management Services
export const permissionService = {
  create: (data) => mockDb.create('rolePermissions', data),
  getAll: () => mockDb.getAll('rolePermissions'),
  getById: (id) => mockDb.getById('rolePermissions', id),
  update: (id, data) => mockDb.update('rolePermissions', id, data),
  delete: (id) => mockDb.delete('rolePermissions', id),
  getByRole: (roleKey) => mockDb.getWhere('rolePermissions', 'role_key', '==', roleKey),
  assignPermission: (roleKey, permission, granted = true) => {
    const existing = mockDb.getWhere('rolePermissions', 'role_key', '==', roleKey)
      .find(p => p.permission === permission);
    
    if (existing) {
      return mockDb.update(existing.id, { granted, updated_at: new Date().toISOString() });
    } else {
      return mockDb.create('rolePermissions', {
        role_key: roleKey,
        permission: permission,
        granted: granted,
        created_at: new Date().toISOString()
      });
    }
  }
};

// Audit Logging Service
export const auditService = {
  create: (data) => mockDb.create('auditLogs', data),
  getAll: () => mockDb.getAll('auditLogs'),
  getByUserId: (userId) => mockDb.getWhere('auditLogs', 'user_id', '==', userId),
  getByAction: (action) => mockDb.getWhere('auditLogs', 'action', '==', action),
  logAction: (userId, action, resource, details = {}) => {
    return mockDb.create('auditLogs', {
      user_id: userId,
      action: action,
      resource: resource,
      details: details,
      timestamp: new Date().toISOString(),
      ip_address: '127.0.0.1', // Mock IP
      user_agent: 'ZeroPath System'
    });
  }
};

// Export the mock database instance for direct access
export { mockDb };

// No sample data per .cursorrules - migration is a no-op
export async function migrateDataToFirestore() {
  console.warn('⚠️ Configure data through the application (no sample data per .cursorrules).');
  return { organization: null, counts: mockDb.getCounts() };
}

// Function to clear all data
export async function clearAllData() {
  try {
    mockDb.clearAll();
    console.log('🎉 All data cleared successfully!');
  } catch (error) {
    console.error('❌ Error clearing data:', error);
    throw error;
  }
}
